import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
	Box, Button, Card, CardContent, FormControlLabel,
	Stack, Switch, TextField, Typography,
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'react-toastify';
import { FileUploadWithManualInput } from '@/AdminComponent/FileUploadWithManualInput';
import { TagsDropdown } from '@/AdminComponent/TagsDropdown';
import { ProductsDropdown } from '@/AdminComponent/ProductsDropdown';
import { InstructorDropdown } from '@/AdminComponent/InstructorDropdown';
import { RelatedVideosDropdown } from '@/AdminComponent/RelatedVideosDropdown';
import { useAtomValue } from 'jotai';
import { userInfoAtom } from '@/data/atoms/utilsAtoms';
import api from '@/data/api';
import ReleaseSchedule from '@/AdminComponent/components/ReleaseSchedule';
import { newYorkMidnight } from '@/utils/helper';
import { LESSON_PLAN_CATEGORY_NAMES, LESSON_PLAN_LABELS } from '@/constants/lessonPlanLabels';


const PDF_PAGE_REGEX = /\/Type\s*\/Page[^s]/g;

const formatFileSize = bytes => {
	if (!bytes || Number.isNaN(Number(bytes))) return '0 KB';
	const sizeInBytes = Number(bytes);
	if (sizeInBytes < 1024) return `${sizeInBytes} B`;
	if (sizeInBytes < 1024 * 1024) return `${(sizeInBytes / 1024).toFixed(1)} KB`;
	return `${(sizeInBytes / (1024 * 1024)).toFixed(2)} MB`;
};

const getEmptyForm = () => ({
	title: '',
	about: '',
	instructor: '',
	pages: '',
	thumbnailLink: '',
	fileUrl: '',
	categories: [],
	tags: [],
	products: [],
	related: [],
	isActive: true,
	isStudioLessonPlan: true,
	releaseDate: null,
});

const LessonPlanForm = () => {
	const [form, setForm] = useState(getEmptyForm());
	const [fetching, setFetching] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [compressingPdf, setCompressingPdf] = useState(false);
	const [pdfCompressed, setPdfCompressed] = useState(false);
	const [pdfOriginalSize, setPdfOriginalSize] = useState(null);
	const [pdfCompressedSize, setPdfCompressedSize] = useState(null);
	const { id } = useParams();
	const navigate = useNavigate();
	const user = useAtomValue(userInfoAtom);
	const isEdit = Boolean(id);

	useEffect(() => {
		api.getCategory().then(res => {
			const studioCategory = res?.data?.find(c => LESSON_PLAN_CATEGORY_NAMES.includes(c.name));
			if (studioCategory) {
				setForm(prev => ({ ...prev, categories: [studioCategory._id] }));
			}
		});
	}, []);

	useEffect(() => {
		if (!isEdit) return;
		setFetching(true);
		api.getLessonPlanById(id)
			.then(res => {
				const p = res?.data;
				setForm({
					title: p?.title || '',
					about: p?.about || '',
					instructor: p?.instructor || '',
					pages: p?.pages || '',
					thumbnailLink: p?.thumbnailLink || '',
					fileUrl: p?.fileUrl || '',
					categories: p?.categoriesDetails?.map(c => c._id) || [],
				tags: p?.tagsDetails?.map(t => t.name).filter(Boolean) || [],
					products: p?.productsData?.map(prod => ({
						_id: prod._id,
						id: prod.id,
						title: prod.title,
						...(prod.variantTitle ? { variantTitle: prod.variantTitle, variant: { id: prod.variantId, title: prod.variantTitle } } : {})
					})) || [],
					related: p?.related?.map(r => r.referralId) || [],
					isActive: p?.isActive !== undefined ? p.isActive : true,
					isStudioLessonPlan: p?.isStudioLessonPlan !== undefined ? p.isStudioLessonPlan : true,
					releaseDate: p?.releaseDate ? new Date(p.releaseDate) : null,
				});
			})
			.catch(() => {
				toast.error('Failed to load lesson plan');
				navigate('/AdminDashboard/lesson-plans');
			})
			.finally(() => setFetching(false));
	}, [id]);

	const setField = (name, value) => setForm(prev => ({ ...prev, [name]: value }));

	const handleCompressClick = async () => {
		if (!form.fileUrl.trim()) {
			toast.error('Please upload a PDF first');
			return;
		}

		if (!/\.pdf(\?.*)?$/i.test(form.fileUrl)) {
			toast.error('The current file is not a PDF');
			return;
		}

		try {
			setCompressingPdf(true);
			const result = await api.compressLessonPlanPdf(form.fileUrl);
			const { cdnLink, originalSize, compressedSize } = result?.data || {};
			if (!cdnLink) throw new Error('Failed to compress PDF');
			setPdfOriginalSize(originalSize);
			setPdfCompressedSize(compressedSize);
			setField('fileUrl', cdnLink);
			setPdfCompressed(true);
			toast.success('PDF compressed successfully');
		} catch (error) {
			toast.error(error?.message || 'Failed to compress PDF');
		} finally {
			setCompressingPdf(false);
		}
	};

	const handlePdfUrlChange = async (url) => {
		setField('fileUrl', url);
		setPdfCompressed(false);
		setPdfOriginalSize(null);
		setPdfCompressedSize(null);
		if (!url) return;
		try {
			const res = await fetch(url);
			const clonedResponse = res.clone();
			const blob = await res.blob();
			setPdfOriginalSize(blob.size);

			const text = await clonedResponse.text();
			const matches = text.match(PDF_PAGE_REGEX);
			setField('pages', matches ? matches.length : '');
		} catch {
			toast.warn('Unable to auto-detect page count from PDF.');
			setField('pages', '');
		}
	};

	const handleSubmit = async () => {
		if (!form.title.trim()) return toast.error('Title is required');
		if (!form.fileUrl.trim()) return toast.error('Please upload a PDF or paste a PDF URL');
		if (!form.pages || Number(form.pages) <= 0) return toast.error('Valid page count is required');

		try {
			setSubmitting(true);
			const payload = { ...form, pages: Number(form.pages) };
			if (isEdit) {
				await api.updateLessonPlan(id, payload);
				toast.success('Lesson plan updated successfully');
			} else {
				await api.createLessonPlan(payload);
				toast.success('Lesson plan created successfully');
			}
			navigate('/AdminDashboard/lesson-plans');
		} catch (err) {
			toast.error(err?.message || 'Something went wrong');
		} finally {
			setSubmitting(false);
		}
	};

	if (fetching) {
		return <Box sx={{ p: 4 }}><Typography>Loading...</Typography></Box>;
	}

	return (
		<Card>
			<CardContent>
				<Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
					{isEdit ? `Edit ${LESSON_PLAN_LABELS.PROGRESSIVE}` : `Add ${LESSON_PLAN_LABELS.PROGRESSIVE}`}
				</Typography>

				<Stack spacing={2}>
					<TextField
						label="Title"
						value={form.title}
						onChange={e => setField('title', e.target.value)}
						fullWidth
					/>

					<Box>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>About</Typography>
						<ReactQuill
							value={form.about}
							onChange={val => setField('about', val)}
							theme="snow"
							modules={{ toolbar: [['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link']] }}
						/>
					</Box>

					<InstructorDropdown
						value={form.instructor}
						onChange={val => setField('instructor', val)}
						token={user?.token}
					/>

					<TextField
						label="Category"
						value={LESSON_PLAN_LABELS.PROGRESSIVE}
						InputProps={{ readOnly: true }}
						fullWidth
					/>

					<TagsDropdown
						value={form.tags}
						onChange={val => setField('tags', val)}
					/>

					<ProductsDropdown
						value={form.products}
						onChange={val => setField('products', val)}
					/>

					<RelatedVideosDropdown
						value={form.related}
						onChange={val => setField('related', val)}
					/>

					<FileUploadWithManualInput
						label="Thumbnail Image"
						accept="image/*"
						currentUrl={form.thumbnailLink}
						onChange={url => setField('thumbnailLink', url)}
						onUpload={url => setField('thumbnailLink', url)}
						placeholder="Enter thumbnail URL or upload image"
					/>

					<FileUploadWithManualInput
						label="PDF File"
						accept="application/pdf"
						currentUrl={form.fileUrl}
						onChange={handlePdfUrlChange}
						onUpload={handlePdfUrlChange}
						placeholder="Enter PDF URL or upload PDF file"
					/>

					<Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
						<Stack spacing={0.5} sx={{ mr: 2 }}>
							<Typography variant="caption" color="text.secondary">
								Actual size: {pdfOriginalSize ? formatFileSize(pdfOriginalSize) : 'Not compressed yet'}
							</Typography>
							<Typography variant="caption" color="text.secondary">
								Compressed size: {pdfCompressedSize ? formatFileSize(pdfCompressedSize) : 'Not compressed yet'}
							</Typography>
						</Stack>
						<Button
							variant="outlined"
							onClick={handleCompressClick}
							disabled={compressingPdf || pdfCompressed || !form.fileUrl.trim()}
						>
							{compressingPdf ? 'Compressing...' : pdfCompressed ? 'Compressed' : 'Compress'}
						</Button>
					</Box>

					<TextField
						label="Number of Pages (auto-detected)"
						type="number"
						inputProps={{ min: 1 }}
						value={form.pages}
						onChange={e => setField('pages', e.target.value)}
						helperText="Auto-detected from PDF when possible; you can override."
						fullWidth
					/>

					<ReleaseSchedule
						releaseDate={form.releaseDate}
						onReleaseDateChange={date => setField('releaseDate', date)}
						newYorkMidnight={newYorkMidnight}
					/>

					<FormControlLabel
						control={<Switch checked={form.isActive} onChange={e => setField('isActive', e.target.checked)} />}
						label="Active"
					/>

					<Box sx={{ display: 'flex', gap: 2 }}>
						<Button variant="contained" onClick={handleSubmit} disabled={submitting}>
							{submitting ? 'Saving...' : isEdit ? 'Update' : 'Save'}
						</Button>
						<Button variant="outlined" onClick={() => setForm(getEmptyForm())} disabled={submitting}>
							Reset
						</Button>
					</Box>
				</Stack>
			</CardContent>
		</Card>
	);
};

export default LessonPlanForm;
